import { fromEvent, merge, Observable } from 'rxjs';
import { share, tap } from 'rxjs/operators';
import {
  getElementsIntersectingRange,
  hasElementsIntersectingRange,
  setElementTagName,
} from '../../helpers/dom/element';
import {
  addBulletedListInRange,
  canMergeList,
  getListType,
  indentListItemsInRange,
  isCursorAtStartOfListItem,
  isListElement,
  isListItemElement,
  ListElement,
  ListOperationDelegate,
  maybeMergeList,
  outdentListItemsInRange,
  splitListItemAtRange,
  TAG_NAMES_BY_LIST_TYPE,
} from '../../helpers/dom/list';
import {
  findAncestor,
  findNextNonWhitespaceSibling,
  findPreviousNonWhitespaceSibling,
} from '../../helpers/dom/node';
import { createPointWithStartOfRange } from '../../helpers/dom/point';
import { fromMutationObserver } from '../../helpers/fromMutationObserver';
import { ignoreDuringCompositions } from '../../helpers/ignoreDuringCompositions';
import { concatIterables, firstInIterable, flatMapIterable, isEmptyIterable } from '../../helpers/iterable';
import { createConnectingService } from '../base/connectingService';
import { ElementSelection, ElementSelectionService } from '../dom/elementSelectionService';
import { ElementService } from '../dom/elementService';
import { ListService, ListType, NULL_LIST_SERVICE } from '../text/listService';
import { HtmlListServiceV2 } from './htmlListServiceV2';

export type HtmlListServiceOptions = {
  newListClassName?: string;
  shouldLineBreakOnShiftEnter?: () => boolean;
  hasListServiceV2?: boolean;
};

export function createHtmlListService(
  element$: Observable<HTMLElement | null>,
  selectionService: ElementSelectionService,
  options: HtmlListServiceOptions = {},
): ListService {
  // New list service is off by default
  if (options.hasListServiceV2)
    return createConnectingService(
      element$,
      () => NULL_LIST_SERVICE,
      element => new HtmlListServiceV2(element, selectionService, options),
    );

  return createConnectingService(
    element$,
    () => NULL_LIST_SERVICE,
    element => new HtmlListService(element, selectionService, options),
  );
}

interface HtmlListState {
  selection: ElementSelection;
  lists: ReadonlyArray<ListElement>;
}

export class HtmlListService extends ElementService<Element> implements ListService {
  constructor(
    element: Element,
    selectionService: ElementSelectionService,
    options: HtmlListServiceOptions = {},
  ) {
    super(
      element,
      {
        effects$: merge(
          fromEvent<KeyboardEvent>(element, 'keydown').pipe(
            ignoreDuringCompositions(element),
            tap(event => this.onKeyDown(event)),
          ),
          fromMutationObserver(element, {
            subtree: true,
            childList: true,
          }).pipe(tap(() => this.onMutate())),
        ).pipe(share()),
      },
      // TODO: Make changes$ more fine-grained.
      selectionService,
    );

    this.selectionService = selectionService;
    this.shouldLineBreakOnShiftEnter = options.shouldLineBreakOnShiftEnter;
    this.listOperationDelegate = {
      decorateList: list => {
        if (options.newListClassName) {
          // eslint-disable-next-line no-param-reassign
          list.className = options.newListClassName;
        }
      },
    };
  }

  private readonly selectionService: ElementSelectionService;
  private readonly shouldLineBreakOnShiftEnter?: () => boolean;
  private shouldMaybeMergeOnMutate = false;
  private listOperationDelegate: ListOperationDelegate;

  private onKeyDown(event: KeyboardEvent): void {
    if (event.defaultPrevented) {
      return;
    }

    const { key } = event;
    switch (key) {
      case 'Tab':
        if (event.shiftKey) {
          if (this.getCanOutdent(key)) {
            event.preventDefault();
            this.outdent();
          }
        } else if (this.getCanIndent(key)) {
          event.preventDefault();
          this.indent();
        }
        break;

      case 'Backspace':
      case 'Delete':
        this.shouldMaybeMergeOnMutate = !this.getCanMerge();
        break;

      case 'Enter':
        this.onEnter(event);
        break;

      default:
    }
  }

  private onMutate() {
    if (this.shouldMaybeMergeOnMutate) {
      this.shouldMaybeMergeOnMutate = false;
      this.maybeMerge();
    }
  }

  private onEnter(event: KeyboardEvent) {
    // Kill switch: override the default browser behavior only if this new option was provided.
    if (!this.shouldLineBreakOnShiftEnter) {
      return;
    }

    const range = this.selectionService.selection?.range;
    if (!range) {
      return;
    }

    // Check whether the selection starts in a list item (regardless of whether it's the anchor or the focus).
    const listItem = findAncestor(range.startContainer, isListItemElement, this.element);
    if (!listItem) {
      return;
    }

    // Override the default browser behavior only if we need to deviate.
    if (!event.shiftKey || this.shouldLineBreakOnShiftEnter()) {
      return;
    }

    event.preventDefault();

    // If we're at the start of the last item, outdent.
    if (!findNextNonWhitespaceSibling(listItem) && isCursorAtStartOfListItem(this.element, range, listItem)) {
      this.outdent();
      return;
    }

    // Otherwise, split the list item.
    const newRange = splitListItemAtRange(this.element, range);
    this.selectionService.selectWithRange(newRange, false);
  }

  private get state(): HtmlListState | undefined {
    const { selection, extraSelectedElements } = this.selectionService;
    if (!selection) {
      return undefined;
    }

    // We don't want to enable list creation across non-contiguous elements.
    if (!isEmptyIterable(extraSelectedElements)) {
      return undefined;
    }

    const lists = getElementsIntersectingRange(this.element, selection.range, isListElement);
    return {
      selection,
      lists: Array.from(lists),
    };
  }

  get type() {
    const { state } = this;
    if (!state) {
      return undefined;
    }

    // Check if all lists in the selection are of the same type.
    const listTypes = new Set(state.lists.map(list => getListType(list)));
    if (listTypes.size !== 1) {
      return undefined;
    }

    return firstInIterable(listTypes);
  }

  setType(newType: ListType | undefined) {
    const { state } = this;

    if (!state) {
      return;
    }

    const { selection, lists } = state;
    const { range, isBackward } = selection;

    // Remove an existing list by outdenting selected items.
    if (!newType) {
      const newRange = outdentListItemsInRange(this.element, range);
      this.selectionService.selectWithRange(newRange, isBackward);
      return;
    }

    // Add a list by indenting selected items/paragraphs/etc.
    if (lists.length === 0) {
      const newRange = addBulletedListInRange(this.element, range, newType, this.listOperationDelegate);
      this.selectionService.selectWithRange(newRange, isBackward);
      return;
    }

    // Change the type of existing lists.
    const newTagName = TAG_NAMES_BY_LIST_TYPE[newType];
    const nestedLists = flatMapIterable(lists, list =>
      concatIterables([list], list.querySelectorAll('ol, ul')),
    );
    this.selectionService.withMutations(() => {
      // Create an array from the iterable, so our mutations don't affect our queries.
      Array.from(nestedLists)
        .filter(list => list.tagName !== newTagName)
        .forEach(list => {
          setElementTagName(list, newTagName);

          if (this.listOperationDelegate.decorateList) {
            this.listOperationDelegate.decorateList(list);
          }
        });
    });
  }

  get canIndent() {
    return this.getCanIndent();
  }

  private getCanIndent(key?: string) {
    return this.getCanChangeIndentLevel(key);
  }

  get canOutdent() {
    return this.getCanOutdent();
  }

  private getCanOutdent(key?: string) {
    return this.getCanChangeIndentLevel(key);
  }

  private getCanChangeIndentLevel(key?: string) {
    const { selection } = this.selectionService;
    if (!selection) {
      return false;
    }

    // Check if there are any list items.
    const { range } = selection;
    if (!hasElementsIntersectingRange(this.element, range, isListItemElement)) {
      return false;
    }

    // Handle backspace only if we are at a collapsed range.
    if (key === 'Backspace' && !range.collapsed) {
      return false;
    }

    // If the selection is collapsed, check we're at the start of a list item.
    if (key && range.collapsed) {
      const item = findAncestor(range.commonAncestorContainer, isListItemElement, this.element);
      return (
        !!item &&
        createPointWithStartOfRange(range).isAtStartOfNode(
          item,
          node => !findPreviousNonWhitespaceSibling(node),
          this.element,
        )
      );
    }

    return true;
  }

  indent() {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { range, isBackward } = selection;
    const newRange = indentListItemsInRange(this.element, range, this.listOperationDelegate);
    this.selectionService.selectWithRange(newRange, isBackward);
  }

  outdent() {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { range, isBackward } = selection;
    const newRange = outdentListItemsInRange(this.element, range);
    this.selectionService.selectWithRange(newRange, isBackward);
  }

  private getCanMerge(): boolean {
    const { selection } = this.selectionService;
    if (!selection) {
      return false;
    }

    const { range } = selection;
    const item = findAncestor(range.endContainer, isListItemElement, this.element);
    if (!item || item.nextSibling || !isListElement(item.parentNode)) {
      return false;
    }

    return canMergeList(item.parentNode);
  }

  private maybeMerge(): void {
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }

    const { range } = selection;
    const item = findAncestor(range.endContainer, isListItemElement, this.element);
    if (!item || item.nextSibling || !isListElement(item.parentNode)) {
      return;
    }

    maybeMergeList(item.parentNode);
  }
}
